import React, { useEffect, useState } from 'react';
import { Button, Empty, notification, message as antdMessage } from 'antd';
import { Plus, Clock, Info } from 'lucide-react';
import { useSelector } from 'react-redux';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/vi';
import { RootState } from '../../configuration';
import {
  useDonateCampaign,
  DonateCampaign,
} from './useDonateCampaign';
import { CreateCampaignModal } from './create-campaign-modal';
import { CampaignDetailPage } from './campaign-detail-page';
import { DottedProgress } from './dotted-progress';

const CARD_SHADOW = '4px 4px 44px rgba(0,0,0,0.08)';

dayjs.extend(relativeTime);
dayjs.locale('vi');

const fmt = (n: number) => `${n}`.replace(/\B(?=(\d{3})+(?!\d))/g, '.');

const statusOf = (c: DonateCampaign) => {
  if (c.closed) return { text: 'Đã đóng', cls: 'text-slate-400' };
  if (c.targetAmount > 0 && c.raised >= c.targetAmount)
    return { text: 'Đã đạt mục tiêu', cls: 'text-emerald-500' };
  if (c.goalType === 'amount_time' && c.endAt && dayjs(c.endAt).isBefore(dayjs()))
    return { text: 'Đã kết thúc', cls: 'text-slate-400' };
  return { text: 'Đang diễn ra', cls: 'text-emerald-500' };
};

const daysLeftLabel = (c: DonateCampaign) => {
  if (c.goalType !== 'amount_time' || !c.endAt) return 'Không giới hạn thời gian';
  const d = dayjs(c.endAt).diff(dayjs(), 'day');
  return d > 0 ? `Còn ${d} ngày` : 'Đã hết hạn';
};

const CampaignCard: React.FC<{ c: DonateCampaign; onOpen: () => void }> = ({
  c,
  onOpen,
}) => {
  const pct =
    c.targetAmount > 0
      ? Math.min(100, Math.round((c.raised / c.targetAmount) * 100))
      : 0;
  const st = statusOf(c);

  return (
    <div
      className="rounded-[20px] bg-white dark:bg-dark2 p-4 flex flex-col gap-3"
      style={{ boxShadow: CARD_SHADOW }}
    >
      {/* Ảnh cover: inset trong padding, bo 20px cả 4 góc (đúng Figma) */}
      <div className="h-[200px] w-full rounded-[20px] overflow-hidden shrink-0">
        {c.imageUrl ? (
          <img src={c.imageUrl} alt={c.title} className="h-full w-full object-cover" />
        ) : (
          <div className="h-full w-full bg-gradient-to-br from-sky-500 to-indigo-600" />
        )}
      </div>

      <div className="flex flex-col gap-2 flex-1 w-full">
        <h3 className="text-[20px] font-semibold leading-7 tracking-tight line-clamp-2">
          {c.title}
        </h3>

        {/* Your progress + % + thời gian + thanh tiến độ */}
        <div className="w-full">
          <div className="text-[10px] font-bold tracking-wide text-[#8D8D8D] uppercase">
            Tiến độ
          </div>
          <div className="flex items-center justify-between mt-0.5">
            <span className="text-[16px] font-bold" style={{ color: '#0072C3' }}>
              Đã đạt {pct}%
            </span>
            <span className="flex items-center gap-1 text-[11px] text-[#8D8D8D]">
              <Clock size={11} /> {dayjs(c.createdAt).fromNow(true)}
            </span>
          </div>
          <DottedProgress pct={pct} className="mt-2" />
        </div>

        <div
          className="text-center text-[18px] font-semibold mt-1"
          style={{ color: 'rgba(25,5,47,0.6)' }}
        >
          {fmt(c.raised)} / {fmt(c.targetAmount)} ca
        </div>

        <div className="flex items-center justify-between text-[15px] mt-1">
          <span className="flex items-center gap-1.5 text-slate-500">
            <Info size={15} /> {daysLeftLabel(c)}
          </span>
          <span className={`font-medium ${st.cls}`}>• {st.text}</span>
        </div>

        <Button
          type="primary"
          block
          className="mt-2 !rounded-full"
          style={{ background: '#066BFF', borderColor: '#066BFF' }}
          onClick={onOpen}
        >
          Xem chi tiết
        </Button>
      </div>
    </div>
  );
};

interface Props {
  profile: any;
  /** chỉ chủ trang mới thấy nút tạo + được rút */
  isOwner?: boolean;
}

export const DonateGoalContent: React.FC<Props> = ({ profile, isOwner = true }) => {
  const ownerId = profile?.userId;
  const ownerName = profile?.fullName || profile?.username || 'username';
  const [notifyApi, notifyHolder] = notification.useNotification();

  // người dùng đang đăng nhập (để gắn tên khi ủng hộ)
  const currentUser = useSelector(
    (state: RootState) => (state as any)?.features?.auth?.profile
  ) as any;
  const currentUserName =
    currentUser?.fullName || currentUser?.username || ownerName;

  const {
    campaigns,
    create,
    updateCampaign,
    addUpdate,
    donate,
    requestWithdraw,
    getById,
  } = useDonateCampaign(ownerId);
  const [createOpen, setCreateOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [selected, setSelected] = useState<DonateCampaign | null>(null);

  // Khi chọn 1 chiến dịch → tải chi tiết (kèm donors/updates) từ BE
  useEffect(() => {
    let alive = true;
    if (!selectedId) {
      setSelected(null);
      return;
    }
    getById(selectedId)
      .then((c) => alive && setSelected(c))
      .catch(() => alive && antdMessage.error('Không tải được chi tiết chiến dịch'));
    return () => {
      alive = false;
    };
  }, [selectedId, getById]);

  // Hiển thị trang Chi tiết chiến dịch (trong tab). Các thao tác trả về DTO mới → cập nhật `selected`.
  if (selectedId && selected) {
    return (
      <CampaignDetailPage
        campaign={selected}
        isOwner={isOwner}
        onBack={() => setSelectedId(null)}
        onDonate={async (amount, msg) => {
          const c = await donate(selectedId, {
            amount,
            message: msg,
            donorName: currentUserName,
          });
          setSelected(c);
        }}
        onWithdraw={async () => {
          const c = await requestWithdraw(selectedId);
          setSelected(c);
        }}
        onPostUpdate={async (content, imageUrl) => {
          const c = await addUpdate(selectedId, {
            content,
            imageUrl,
            authorName: currentUserName,
          });
          setSelected(c);
        }}
        onEdit={async (patch) => {
          const c = await updateCampaign(selectedId, patch);
          setSelected(c);
        }}
      />
    );
  }

  return (
    <div className="py-2">
      {notifyHolder}
      <div className="flex items-center justify-between gap-3 mb-5">
        <h2 className="text-2xl font-bold">
          Quyên góp của <span className="text-[#234093]">@ {ownerName}</span>
        </h2>
        {isOwner && (
          <Button
            type="primary"
            size="large"
            icon={<Plus size={18} />}
            className="!rounded-full"
            style={{ background: '#066BFF', borderColor: '#066BFF' }}
            onClick={() => setCreateOpen(true)}
          >
            Tạo chiến dịch
          </Button>
        )}
      </div>

      {campaigns.length === 0 ? (
        <div className="py-16">
          <Empty
            description={
              isOwner
                ? 'Bạn chưa có chiến dịch quyên góp nào. Hãy tạo chiến dịch đầu tiên!'
                : 'Người dùng chưa có chiến dịch quyên góp nào.'
            }
          />
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
          {campaigns.map((c) => (
            <CampaignCard key={c.id} c={c} onOpen={() => setSelectedId(c.id)} />
          ))}
        </div>
      )}

      <CreateCampaignModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreate={async (v) => {
          setCreateOpen(false);
          try {
            await create({ ...v, ownerName });
            notifyApi.success({
              message: 'Tạo chiến dịch thành công',
              description: `Chiến dịch "${v.title}" đã được tạo.`,
              placement: 'topRight',
            });
          } catch (e: any) {
            antdMessage.error(
              e?.response?.data?.message || 'Tạo chiến dịch thất bại'
            );
          }
        }}
      />
    </div>
  );
};

export default DonateGoalContent;
